import React, { useEffect } from "react";
import {
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { withTranslation } from "react-i18next";
import { scrollToFirstFormikError } from "../../../helpers/utils";

const AddReleaseNoteModal = ({ releaseNote, isModalOpen, onCancel, onSave, loader, t }) => {
  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,

    initialValues: {
      version: releaseNote?.version || "",
      title_en: releaseNote?.title_en || "",
      title_pt: releaseNote?.title_pt || "",
      release_date: releaseNote?.release_date || "",
      is_published: releaseNote?.is_published || false,
      features: releaseNote?.features || [
        { title_en: "", title_pt: "", description_en: "", description_pt: "", icon: "ri-star-line", type: "new_feature" },
      ],
    },
    validationSchema: Yup.object({
      version: Yup.string().required(
        `${t("common.please")} ${t("common.enter")} ${t("releaseNotes.version")}`
      ),
      title_en: Yup.string().required(
        `${t("common.please")} ${t("common.enter")} ${t("releaseNotes.titleEn")}`
      ),
      title_pt: Yup.string().required(
        `${t("common.please")} ${t("common.enter")} ${t("releaseNotes.titlePt")}`
      ),
      release_date: Yup.string().required(
        `${t("common.please")} ${t("common.enter")} ${t("releaseNotes.releaseDate")}`
      ),
      features: Yup.array().of(
        Yup.object({
          title_en: Yup.string().required(
            `${t("common.please")} ${t("common.enter")} ${t("releaseNotes.featureTitleEn")}`
          ),
          title_pt: Yup.string().required(
            `${t("common.please")} ${t("common.enter")} ${t("releaseNotes.featureTitlePt")}`
          ),
        })
      ),
    }),
    onSubmit: (values) => {
      onSave(values, validation);
    },
  });

  useEffect(() => {
    validation.resetForm();
  }, [isModalOpen]);

  const handleModalClose = () => {
    if (!loader) {
      validation.resetForm();
      onCancel();
    }
  };

  const handleAddFeature = () => {
    const features = [...validation.values.features];
    features.push({ title_en: "", title_pt: "", description_en: "", description_pt: "", icon: "ri-star-line", type: "new_feature" });
    validation.setFieldValue("features", features);
  };

  const handleRemoveFeature = (index) => {
    const features = [...validation.values.features];
    features.splice(index, 1);
    validation.setFieldValue("features", features);
  };

  const handleFeatureChange = (index, field, value) => {
    const features = [...validation.values.features];
    features[index] = { ...features[index], [field]: value };
    validation.setFieldValue("features", features);
  };

  return (
    <Modal centered isOpen={isModalOpen} toggle={handleModalClose} fade={false} size="lg">
      <ModalHeader>
        {releaseNote?.id ? t("releaseNotes.editReleaseNote") : t("releaseNotes.addReleaseNote")}
      </ModalHeader>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalBody>
          <div className="row">
            <div className="col-md-4 mb-3">
              <Label htmlFor="version" className="form-label">
                {t("releaseNotes.version")}
              </Label>
              <Input
                name="version"
                className="form-control"
                placeholder={`${t("common.enter")} ${t("releaseNotes.version")}`}
                type="text"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.version || ""}
                invalid={validation.touched.version && validation.errors.version ? true : false}
              />
              {validation.touched.version && validation.errors.version ? (
                <FormFeedback type="invalid">{validation.errors.version}</FormFeedback>
              ) : null}
            </div>
            <div className="col-md-4 mb-3">
              <Label htmlFor="release_date" className="form-label">
                {t("releaseNotes.releaseDate")}
              </Label>
              <Input
                name="release_date"
                className="form-control"
                type="date"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.release_date || ""}
                invalid={validation.touched.release_date && validation.errors.release_date ? true : false}
              />
              {validation.touched.release_date && validation.errors.release_date ? (
                <FormFeedback type="invalid">{validation.errors.release_date}</FormFeedback>
              ) : null}
            </div>
            <div className="col-md-4 mb-3 d-flex align-items-end">
              <div className="form-check form-switch">
                <Input
                  name="is_published"
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="is_published"
                  onChange={validation.handleChange}
                  checked={validation.values.is_published}
                />
                <Label className="form-check-label" htmlFor="is_published">
                  {t("releaseNotes.published")}
                </Label>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <Label htmlFor="title_en" className="form-label">
                {t("releaseNotes.titleEn")}
              </Label>
              <Input
                name="title_en"
                className="form-control"
                placeholder={`${t("common.enter")} ${t("releaseNotes.titleEn")}`}
                type="text"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.title_en || ""}
                invalid={validation.touched.title_en && validation.errors.title_en ? true : false}
              />
              {validation.touched.title_en && validation.errors.title_en ? (
                <FormFeedback type="invalid">{validation.errors.title_en}</FormFeedback>
              ) : null}
            </div>
            <div className="col-md-6 mb-3">
              <Label htmlFor="title_pt" className="form-label">
                {t("releaseNotes.titlePt")}
              </Label>
              <Input
                name="title_pt"
                className="form-control"
                placeholder={`${t("common.enter")} ${t("releaseNotes.titlePt")}`}
                type="text"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.title_pt || ""}
                invalid={validation.touched.title_pt && validation.errors.title_pt ? true : false}
              />
              {validation.touched.title_pt && validation.errors.title_pt ? (
                <FormFeedback type="invalid">{validation.errors.title_pt}</FormFeedback>
              ) : null}
            </div>
          </div>

          {/* Features Section */}
          <div className="d-flex justify-content-between align-items-center mb-3 mt-2">
            <h6 className="mb-0">{t("releaseNotes.features")}</h6>
            <Button color="soft-success" size="sm" type="button" onClick={handleAddFeature}>
              <i className="ri-add-fill me-1" />
              {t("releaseNotes.addFeature")}
            </Button>
          </div>

          {validation.values.features.map((feature, index) => (
            <div key={index} className="border rounded p-3 mb-3 position-relative">
              <button
                type="button"
                className="btn btn-sm btn-ghost-danger position-absolute"
                style={{ top: "4px", right: "4px" }}
                onClick={() => handleRemoveFeature(index)}
                disabled={validation.values.features.length <= 1}
              >
                <i className="ri-delete-bin-line" />
              </button>

              <div className="row">
                <div className="col-md-4 mb-2">
                  <Label className="form-label">{t("releaseNotes.featureTitleEn")}</Label>
                  <Input
                    type="text"
                    className="form-control"
                    placeholder={`${t("common.enter")} ${t("releaseNotes.featureTitleEn")}`}
                    value={feature.title_en}
                    onChange={(e) => handleFeatureChange(index, "title_en", e.target.value)}
                    invalid={
                      validation.touched.features?.[index]?.title_en &&
                      validation.errors.features?.[index]?.title_en
                        ? true
                        : false
                    }
                  />
                  {validation.touched.features?.[index]?.title_en &&
                  validation.errors.features?.[index]?.title_en ? (
                    <FormFeedback type="invalid">
                      {validation.errors.features[index].title_en}
                    </FormFeedback>
                  ) : null}
                </div>
                <div className="col-md-4 mb-2">
                  <Label className="form-label">{t("releaseNotes.featureTitlePt")}</Label>
                  <Input
                    type="text"
                    className="form-control"
                    placeholder={`${t("common.enter")} ${t("releaseNotes.featureTitlePt")}`}
                    value={feature.title_pt}
                    onChange={(e) => handleFeatureChange(index, "title_pt", e.target.value)}
                    invalid={
                      validation.touched.features?.[index]?.title_pt &&
                      validation.errors.features?.[index]?.title_pt
                        ? true
                        : false
                    }
                  />
                  {validation.touched.features?.[index]?.title_pt &&
                  validation.errors.features?.[index]?.title_pt ? (
                    <FormFeedback type="invalid">
                      {validation.errors.features[index].title_pt}
                    </FormFeedback>
                  ) : null}
                </div>
                <div className="col-md-2 mb-2">
                  <Label className="form-label">{t("releaseNotes.type")}</Label>
                  <Input
                    type="select"
                    className="form-control"
                    value={feature.type || "new_feature"}
                    onChange={(e) => handleFeatureChange(index, "type", e.target.value)}
                  >
                    <option value="new_feature">{t("releaseNotes.newFeature")}</option>
                    <option value="improvement">{t("releaseNotes.improvement")}</option>
                    <option value="bug">{t("releaseNotes.bugFix")}</option>
                  </Input>
                </div>
                <div className="col-md-2 mb-2">
                  <Label className="form-label">{t("releaseNotes.icon")}</Label>
                  <Input
                    type="text"
                    className="form-control"
                    placeholder="ri-star-line"
                    value={feature.icon}
                    onChange={(e) => handleFeatureChange(index, "icon", e.target.value)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-2">
                  <Label className="form-label">{t("releaseNotes.descriptionEn")}</Label>
                  <Input
                    type="textarea"
                    className="form-control"
                    rows="2"
                    placeholder={`${t("common.enter")} ${t("releaseNotes.descriptionEn")}`}
                    value={feature.description_en}
                    onChange={(e) => handleFeatureChange(index, "description_en", e.target.value)}
                  />
                </div>
                <div className="col-md-6 mb-2">
                  <Label className="form-label">{t("releaseNotes.descriptionPt")}</Label>
                  <Input
                    type="textarea"
                    className="form-control"
                    rows="2"
                    placeholder={`${t("common.enter")} ${t("releaseNotes.descriptionPt")}`}
                    value={feature.description_pt}
                    onChange={(e) => handleFeatureChange(index, "description_pt", e.target.value)}
                  />
                </div>
              </div>
            </div>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button color="dark" onClick={handleModalClose} disabled={loader}>
            {t("common.cancel")}
          </Button>
          <Button
            color="primary"
            disabled={loader}
            className="btn-load d-flex"
            type="submit"
            onClick={() => scrollToFirstFormikError(validation.errors)}
          >
            {loader ? (
              <span className="d-flex align-items-center ">
                <Spinner size="xs" className="flex-shrink-0"></Spinner>
              </span>
            ) : null}
            <span className={`flex-grow-1 ${loader ? "ms-2" : ""}`}>{t("common.save")}</span>
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default withTranslation()(AddReleaseNoteModal);
